/**
 * Property Location panel (tracker item 41, phase 1): the geocode status,
 * coordinates, a link to OpenStreetMap (with attribution), and the office's
 * "Retry" and "Set manually" actions. Shown to office roles only (the API
 * leaves coordinates out for everyone else). No embedded map in v1.
 */

import React, { useEffect, useRef, useState } from 'react';
import * as types from '../api/types';
import { IGeocodingApi } from '../api';
import { getErrorMessage } from '../utils/apiError';
import { inputClass, labelClass, primaryButtonClass, secondaryButtonClass } from './formStyles';
import { GeocodeStatusChip } from './GeocodeStatusChip';
import { Toast } from './Toast';

type LocationFields = Pick<types.Property, 'latitude' | 'longitude' | 'geocodeStatus' | 'geocodePrecision'>;

interface PropertyLocationPanelProps {
  property: types.Property;
  api: IGeocodingApi;
  /** The property's new location fields after an action */
  onChanged: (fields: LocationFields) => void;
  className: string;
  headingClassName: string;
}

const STATUS_HELP: Record<types.GeocodeStatus, string> = {
  PENDING: 'The address is being looked up in the background.',
  OK: '',
  NOT_FOUND: 'The address could not be found on the map. Check it, or set the location by hand.',
  FAILED: 'The lookup failed. It is retried automatically, or you can retry now.',
  MANUAL: 'Set by hand. Changing the address clears it.',
  SKIPPED: 'Address lookup is turned off on this server. You can set the location by hand.',
};

/** "39.781721, -89.650148" (or with a space, a semicolon, or from a map link) -> numbers; null when it does not read as a pair. */
export function parseCoordinatePair(text: string): { latitude: number; longitude: number } | null {
  // An OpenStreetMap link: #map=<zoom>/<lat>/<lng>
  const osm = text.match(/map=\d+\/(-?\d{1,3}(?:\.\d+)?)\/(-?\d{1,3}(?:\.\d+)?)/);
  const match = osm ?? text.match(/(-?\d{1,3}(?:\.\d+)?)\s*[,;/ ]\s*(-?\d{1,3}(?:\.\d+)?)/);
  if (!match) return null;
  const latitude = Number(match[1]);
  const longitude = Number(match[2]);
  if (Math.abs(latitude) > 90 || Math.abs(longitude) > 180) return null;
  return { latitude, longitude };
}

export function osmLink(latitude: number, longitude: number): string {
  return `https://www.openstreetmap.org/?mlat=${latitude}&mlon=${longitude}#map=17/${latitude}/${longitude}`;
}

export function PropertyLocationPanel({ property, api, onChanged, className, headingClassName }: PropertyLocationPanelProps) {
  const status: types.GeocodeStatus = property.geocodeStatus ?? 'PENDING';
  const hasCoordinates = property.latitude != null && property.longitude != null;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const apply = (location: types.PropertyLocation) =>
    onChanged({
      latitude: location.latitude,
      longitude: location.longitude,
      geocodeStatus: location.geocodeStatus,
      geocodePrecision: location.geocodePrecision,
    });

  const run = async (action: () => Promise<types.PropertyLocation>, message: string) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      apply(await action());
      setNotice(message);
      return true;
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to update the location'));
      return false;
    } finally {
      setBusy(false);
    }
  };

  return (
    <section aria-labelledby="property-location-heading" className={className}>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 id="property-location-heading" className={headingClassName}>Location</h2>
          <p className="mt-1 text-sm text-gray-500">Used to estimate travel between jobs.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className={`${secondaryButtonClass} min-h-[44px] sm:min-h-0`}
            disabled={busy}
            onClick={() => run(() => api.requeuePropertyGeocode(property.id), 'The address will be looked up again shortly.')}
          >
            Retry
          </button>
          <button type="button" className={`${secondaryButtonClass} min-h-[44px] sm:min-h-0`} disabled={busy} onClick={() => setDialogOpen(true)}>
            Set manually
          </button>
          {status === 'MANUAL' && (
            <button
              type="button"
              className={`${secondaryButtonClass} min-h-[44px] sm:min-h-0`}
              disabled={busy}
              onClick={() => run(() => api.setPropertyLocation(property.id, null), 'The hand-set location was cleared; the address will be looked up again.')}
            >
              Clear pin
            </button>
          )}
        </div>
      </div>

      {error && <Toast kind="error">{error}</Toast>}
      {notice && <Toast kind="success">{notice}</Toast>}

      <div className="flex flex-wrap items-center gap-2 text-sm">
        <GeocodeStatusChip status={status} precision={property.geocodePrecision} />
        {hasCoordinates ? (
          <>
            <span className="font-mono text-gray-900">
              {property.latitude!.toFixed(6)}, {property.longitude!.toFixed(6)}
            </span>
            <a
              href={osmLink(property.latitude!, property.longitude!)}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-700 hover:underline"
            >
              Open in OpenStreetMap
            </a>
          </>
        ) : (
          <span className="text-gray-500">No coordinates yet</span>
        )}
      </div>
      {status === 'OK' && property.geocodePrecision === 'POSTCODE' && (
        <p className="mt-2 text-sm text-gray-600">Approximate: only the postal code area was found, so travel estimates are rough.</p>
      )}
      {STATUS_HELP[status] && <p className="mt-2 text-sm text-gray-600">{STATUS_HELP[status]}</p>}
      <p className="mt-3 text-xs text-gray-500">
        Map data ©{' '}
        <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer" className="underline">
          OpenStreetMap contributors
        </a>
      </p>

      {dialogOpen && (
        <SetLocationDialog
          initial={hasCoordinates ? { latitude: property.latitude!, longitude: property.longitude! } : null}
          busy={busy}
          onCancel={() => setDialogOpen(false)}
          onSave={async (location) => {
            if (await run(() => api.setPropertyLocation(property.id, location), 'Location saved.')) setDialogOpen(false);
          }}
        />
      )}
    </section>
  );
}

interface SetLocationDialogProps {
  initial: { latitude: number; longitude: number } | null;
  busy: boolean;
  onCancel: () => void;
  onSave: (location: { latitude: number; longitude: number }) => void;
}

function SetLocationDialog({ initial, busy, onCancel, onSave }: SetLocationDialogProps) {
  const [latitude, setLatitude] = useState(initial ? String(initial.latitude) : '');
  const [longitude, setLongitude] = useState(initial ? String(initial.longitude) : '');
  const [paste, setPaste] = useState('');
  const [error, setError] = useState<string | null>(null);
  const firstField = useRef<HTMLInputElement>(null);

  useEffect(() => {
    firstField.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !busy) onCancel();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [busy, onCancel]);

  const applyPasted = (text: string) => {
    setPaste(text);
    const pair = parseCoordinatePair(text);
    if (pair) {
      setLatitude(String(pair.latitude));
      setLongitude(String(pair.longitude));
      setError(null);
    }
  };

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    const lat = Number(latitude.trim());
    const lng = Number(longitude.trim());
    if (latitude.trim() === '' || longitude.trim() === '' || !Number.isFinite(lat) || !Number.isFinite(lng) || Math.abs(lat) > 90 || Math.abs(lng) > 180) {
      setError('Enter a latitude from -90 to 90 and a longitude from -180 to 180.');
      return;
    }
    onSave({ latitude: lat, longitude: lng });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4" role="presentation">
      <form
        role="dialog"
        aria-modal="true"
        aria-labelledby="set-location-title"
        onSubmit={submit}
        noValidate
        className="max-h-full w-full overflow-y-auto rounded-t-lg bg-white p-5 shadow-xl sm:max-w-md sm:rounded-lg"
      >
        <h2 id="set-location-title" className="text-lg font-semibold text-gray-900">Set location by hand</h2>
        <p className="mt-1 text-sm text-gray-600">
          Find the property on openstreetmap.org (right-click, "Show address") or another map, copy its coordinates and paste them here.
          A hand-set location is kept until the address changes.
        </p>
        <div className="mt-4 space-y-4">
          <div>
            <label htmlFor="location-paste" className={labelClass}>Paste coordinates</label>
            <input
              id="location-paste"
              ref={firstField}
              value={paste}
              onChange={(e) => applyPasted(e.target.value)}
              placeholder="39.781721, -89.650148"
              className={inputClass}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="location-latitude" className={labelClass}>Latitude</label>
              <input id="location-latitude" inputMode="decimal" value={latitude} onChange={(e) => setLatitude(e.target.value)} className={inputClass} />
            </div>
            <div>
              <label htmlFor="location-longitude" className={labelClass}>Longitude</label>
              <input id="location-longitude" inputMode="decimal" value={longitude} onChange={(e) => setLongitude(e.target.value)} className={inputClass} />
            </div>
          </div>
          {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
        </div>
        <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button type="button" className={`${secondaryButtonClass} min-h-[44px] sm:min-h-0`} onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button type="submit" className={`${primaryButtonClass} min-h-[44px] sm:min-h-0`} disabled={busy}>
            {busy ? 'Saving...' : 'Save location'}
          </button>
        </div>
      </form>
    </div>
  );
}
